import React, { useState, useEffect, useMemo } from "react";
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  CreativeReportData,
  CreativeScreenPlayInfo,
  ReportFormat,
} from "../../schemas/reportSchema";
import { reportService } from "../../api/services/reportService";
import { toast } from "react-toastify";
import FilePreview from "../common/FilePreview";
import { getFullMediaUrl } from "../../utils/mediaUtils";
import {
  countDisplayDaysInclusive,
  formatDisplayDate,
} from "../../utils/dateUtils";

/** Wall-clock seconds in a day -- the ceiling one screen can play in 24 hours. */
const SECONDS_PER_DAY = 86400;

/**
 * Screen name given to a row the operator adds in the modal.
 *
 * The generated file prints Restaurant Name and never the screen name, so this
 * is only the key the server merges on. The prefix keeps an added row from
 * colliding with a real screen and silently editing it instead.
 */
const ADDED_ROW_PREFIX = "__added__";
const addedRowKey = (n: number) => `${ADDED_ROW_PREFIX}${n}`;

/**
 * Whether a row was added here rather than returned by the server.
 *
 * Keyed off the row itself, not its position. Rows can now be deleted, so an
 * index-based test ("anything past the original row count") starts pointing at
 * the wrong rows the moment a server row is removed and everything after it
 * shifts down -- an added row would quietly lose its editable labels.
 */
const isAddedRow = (row: CreativeScreenPlayInfo) =>
  row.screenName?.startsWith(ADDED_ROW_PREFIX) ?? false;

interface EditableCreativeReportProps {
  isOpen: boolean;
  onClose: () => void;
  reportData: CreativeReportData;
  creativeId: string;
  filters?: {
    startDate?: string | null;
    endDate?: string | null;
    cityId?: string | null;
    regionId?: string | null;
  };
  format?: ReportFormat;
}

const EditableCreativeReport: React.FC<EditableCreativeReportProps> = ({
  isOpen,
  onClose,
  reportData,
  creativeId,
  filters,
  format = "PDF",
}) => {
  const [editableData, setEditableData] = useState<CreativeReportData>(reportData);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(true); // Default to true
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  /**
   * Raw text for the cell currently being typed in, so a field can sit empty
   * while the user retypes it.
   *
   * The inputs are bound to numbers, and an empty box has no number -- so
   * clearing one renders `0` and the next digit lands behind it ("01000").
   * Holding the raw string until blur lets the box be genuinely empty; on blur
   * the draft is dropped and the canonical number renders again, which also
   * tidies up anything like "007".
   */
  const [cellDrafts, setCellDrafts] = useState<Record<string, string>>({});
  /**
   * Seconds of screen time one play consumes -- the number shown as "Average Ad
   * repetition frequency", and now editable.
   *
   * One value for the whole report rather than one per row. Per-row lengths
   * meant two screens with the same play count printed different durations,
   * because each had measured a slightly different average. An ad is the same
   * length everywhere, so operators set it once and every row follows.
   *
   * Seeded from what the server measured, which is the honest starting point;
   * an operator who knows the spot is 15s can correct a measured 12s and have
   * the table recalculate around it.
   */
  const [perPlaySeconds, setPerPlaySeconds] = useState<number>(0);
  /** Raw text while the average is being typed, so it can hold "7." or "". */
  const [averageDraft, setAverageDraft] = useState<string | null>(null);
  /** Counter behind addedRowKey, so two added rows never share a key. */
  const [addedRowCount, setAddedRowCount] = useState<number>(0);

  // Update editableData when reportData changes
  useEffect(() => {
    setEditableData(reportData);
    // Clear row errors and in-progress text when data is reset
    setRowErrors({});
    setCellDrafts({});
    setAverageDraft(null);
    setAddedRowCount(0);
    setPerPlaySeconds(
      reportData.numberOfTimesPlayed > 0
        ? reportData.totalScreenTime / reportData.numberOfTimesPlayed
        : 0
    );
  }, [reportData]);

  // Reset to edit mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsEditing(true);
      // Clear row errors and in-progress text when modal opens
      setRowErrors({});
      setCellDrafts({});
    }
  }, [isOpen]);

  /**
   * The hard ceiling on a single row's screen time: a screen cannot accumulate
   * more playtime than there is wall-clock time in the reporting window.
   *
   * Null when the report has no bounded date range -- there is nothing to
   * validate against then, so the cap is simply not applied.
   */
  const maxRowDuration = useMemo(() => {
    const days = countDisplayDaysInclusive(
      reportData.startDate,
      reportData.endDate
    );
    return days === null ? null : days * SECONDS_PER_DAY;
  }, [reportData.startDate, reportData.endDate]);

  // Auto-calculate totals from table data
  const calculatedTotals = useMemo(() => {
    const totalScreenTime = editableData.screensPlayed.reduce(
      (sum, screen) => sum + (screen.duration || 0),
      0
    );
    const numberOfTimesPlayed = editableData.screensPlayed.reduce(
      (sum, screen) => sum + (screen.numberOfTimesPlayed || 0),
      0
    );
    const totalScreensPlayed = editableData.screensPlayed.length;
    // Average Frequency = Total Screen Time / Total Number of Times Played.
    //
    // Must stay identical to the backend's formula, which is what actually
    // prints in the downloaded file. Dividing by plays-per-screen instead makes
    // the two agree only for single-screen reports and diverge by a factor of
    // the screen count for every other one -- so the preview would misstate the
    // very number the user opened this modal to check before sending it on.
    const averageRepetitionFrequency =
      numberOfTimesPlayed > 0 ? totalScreenTime / numberOfTimesPlayed : 0;

    return {
      totalScreenTime,
      numberOfTimesPlayed,
      totalScreensPlayed,
      averageRepetitionFrequency,
    };
  }, [editableData.screensPlayed]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}hr ${minutes}min ${secs}sec`;
    }
    if (minutes > 0) {
      return `${minutes}min ${secs}sec`;
    }
    return `${secs}sec`;
  };

  const formatAverageFrequency = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (minutes > 0) {
      return `${minutes}min ${secs}sec`;
    }
    return `${secs}sec`;
  };

  /**
   * Re-derives a row's partner field from the one the user just typed.
   *
   * Duration and plays are two views of the same quantity, so editing one in
   * isolation is what let the report claim, say, twice the screen time for an
   * unchanged play count -- an average per play that the creative's own length
   * makes impossible. Holding `perPlaySeconds` fixed and recomputing the other
   * field keeps every number on screen describing the same reality.
   *
   * The typed field is left exactly as entered rather than being snapped back
   * to `plays x perPlaySeconds`. Snapping would rewrite digits under the
   * user's cursor mid-typing; the small drift it avoids is carried honestly by
   * the average-frequency figure instead.
   */
  const applyLinkedRowEdit = (
    row: CreativeScreenPlayInfo,
    field: "duration" | "numberOfTimesPlayed",
    value: number,
    perPlaySeconds: number | null
  ): CreativeScreenPlayInfo => {
    const next = { ...row, [field]: value };

    if (!perPlaySeconds || perPlaySeconds <= 0) {
      return next;
    }

    if (field === "duration") {
      next.numberOfTimesPlayed = Math.max(
        0,
        Math.round(value / perPlaySeconds)
      );
    } else {
      next.duration = Math.max(0, Math.round(value * perPlaySeconds));
    }

    return next;
  };

  /**
   * Flags a row whose screen time exceeds the wall-clock time available in the
   * reporting window. Linkage keeps the two fields consistent with each other;
   * this is the separate guard against a pair that is consistent but still
   * physically impossible for the number of days being reported on.
   */
  const validateRow = (
    row: CreativeScreenPlayInfo,
    maxDuration: number | null,
    added: boolean
  ): string | undefined => {
    // An added row with no name prints as a blank line in a client's file.
    if (added && !row.restaurantName?.trim()) {
      return "Enter a restaurant name for this row";
    }
    if (maxDuration !== null && row.duration > maxDuration) {
      return `Cannot exceed ${formatDuration(maxDuration)} -- the total time in the report's date range`;
    }
    return undefined;
  };

  /** Re-runs validation over every row; used after edits that touch them all. */
  const revalidateAll = (rows: CreativeScreenPlayInfo[]) => {
    const errors: Record<number, string> = {};
    rows.forEach((row, index) => {
      const message = validateRow(row, maxRowDuration, isAddedRow(row));
      if (message) errors[index] = message;
    });
    setRowErrors(errors);
  };

  /**
   * Restates every row's screen time when the average is edited.
   *
   * Play counts are held: they are hard counts of what the players logged, so
   * the length of a play is the figure to correct, not how many there were.
   */
  const handleAverageInput = (raw: string) => {
    setAverageDraft(raw);
    const value = Math.max(0, parseFloat(raw) || 0);
    setPerPlaySeconds(value);
    if (value <= 0) return;

    const rows = editableData.screensPlayed.map((row) => ({
      ...row,
      duration: Math.max(0, Math.round(row.numberOfTimesPlayed * value)),
    }));
    setEditableData((prev) => ({ ...prev, screensPlayed: rows }));
    setCellDrafts({});
    revalidateAll(rows);
  };

  /** Appends a blank row that exists only in the file this modal generates. */
  const handleAddRow = () => {
    const rows: CreativeScreenPlayInfo[] = [
      ...editableData.screensPlayed,
      {
        screenName: addedRowKey(addedRowCount),
        restaurantName: "",
        city: "",
        region: "",
        duration: 0,
        numberOfTimesPlayed: 0,
      },
    ];
    setEditableData((prev) => ({ ...prev, screensPlayed: rows }));
    setAddedRowCount((n) => n + 1);
    // Validate straight away. Without this a blank row carries no error until
    // something in it is touched, so Confirm would accept it and the client's
    // file would print an unnamed line.
    revalidateAll(rows);
  };

  const handleRemoveRow = (index: number) => {
    const rows = editableData.screensPlayed.filter((_, i) => i !== index);
    setEditableData((prev) => ({ ...prev, screensPlayed: rows }));
    setCellDrafts({});
    revalidateAll(rows);
  };

  /** Restaurant / region / city on an added row. */
  const handleRowTextChange = (
    index: number,
    field: "restaurantName" | "region" | "city",
    value: string
  ) => {
    const rows = [...editableData.screensPlayed];
    rows[index] = { ...rows[index], [field]: value };
    setEditableData((prev) => ({ ...prev, screensPlayed: rows }));

    setRowErrors((errors) => {
      const message = validateRow(rows[index], maxRowDuration, true);
      const next = { ...errors };
      if (message) next[index] = message;
      else delete next[index];
      return next;
    });
  };

  const cellKey = (index: number, field: string) => `${index}:${field}`;

  /** What a cell shows: the in-progress text if there is one, else the number. */
  const cellValue = (
    index: number,
    field: "duration" | "numberOfTimesPlayed",
    value: number
  ) => cellDrafts[cellKey(index, field)] ?? String(value);

  /** Drops the draft so the cell falls back to rendering the stored number. */
  const handleCellBlur = (
    index: number,
    field: "duration" | "numberOfTimesPlayed"
  ) => {
    setCellDrafts((drafts) => {
      const next = { ...drafts };
      delete next[cellKey(index, field)];
      return next;
    });
  };

  const handleCellInput = (
    index: number,
    field: "duration" | "numberOfTimesPlayed",
    raw: string
  ) => {
    // Only the cell being typed into keeps raw text -- replacing the map
    // rather than adding to it. A draft left on another cell would keep
    // rendering the text last typed there and hide the value this edit just
    // derived for it, which is the whole point of the two fields being linked.
    setCellDrafts({ [cellKey(index, field)]: raw });
    // An empty box is worth 0 to the totals, but stays empty on screen.
    handleScreenRowChange(index, field, Math.max(0, parseInt(raw, 10) || 0));
  };

  const handleScreenRowChange = (
    index: number,
    field: "duration" | "numberOfTimesPlayed",
    value: number
  ) => {
    const updatedRow = applyLinkedRowEdit(
      editableData.screensPlayed[index],
      field,
      value,
      perPlaySeconds
    );

    setEditableData((prev) => {
      const updatedScreens = [...prev.screensPlayed];
      updatedScreens[index] = updatedRow;
      return {
        ...prev,
        screensPlayed: updatedScreens,
      };
    });

    setRowErrors((errors) => {
      const message = validateRow(updatedRow, maxRowDuration, isAddedRow(updatedRow));
      const nextErrors = { ...errors };
      if (message) {
        nextErrors[index] = message;
      } else {
        delete nextErrors[index];
      }
      return nextErrors;
    });
  };

  // Check if data has been edited. Dates are read-only in this modal, so only
  // the per-screen figures can differ from what the server returned.
  const hasEdits = useMemo(() => {
    // Check if any screen data changed
    if (editableData.screensPlayed.length !== reportData.screensPlayed.length) {
      return true;
    }

    for (let i = 0; i < editableData.screensPlayed.length; i++) {
      const edited = editableData.screensPlayed[i];
      const original = reportData.screensPlayed[i];

      if (!original) return true;

      if (
        edited.duration !== original.duration ||
        edited.numberOfTimesPlayed !== original.numberOfTimesPlayed
      ) {
        return true;
      }
    }

    return false;
  }, [editableData, reportData]);

  const handleConfirm = () => {
    if (Object.keys(rowErrors).length > 0) {
      toast.error("Please fix the highlighted values before confirming");
      return;
    }
    setIsEditing(false);
  };

  const formatLabel = format === "EXCEL" ? "Excel" : "PDF";

  const handleGenerateReport = async () => {
    if (isEditing) {
      toast.error(`Please confirm your changes before generating ${formatLabel}`);
      return;
    }

    setIsGeneratingPDF(true);
    try {
      toast.info(`Generating ${formatLabel}...`);

      let downloadResult;

      if (hasEdits) {
        // Use mutation with edited data
        // The label fields are only read for rows whose screenName matches no
        // screen in the report -- the ones added here. Sending them for every
        // row keeps the payload uniform; the server ignores them otherwise.
        const screenEdits = editableData.screensPlayed.map((screen) => ({
          screenName: screen.screenName,
          duration: screen.duration,
          numberOfTimesPlayed: screen.numberOfTimesPlayed,
          restaurantName: screen.restaurantName ?? null,
          city: screen.city ?? null,
          region: screen.region ?? null,
        }));

        downloadResult = await reportService.downloadCreativeReportWithEdits({
          creativeId,
          startDate: editableData.startDate,
          endDate: editableData.endDate,
          cityId: filters?.cityId || null,
          regionId: filters?.regionId || null,
          totalScreenTime: calculatedTotals.totalScreenTime,
          numberOfTimesPlayed: calculatedTotals.numberOfTimesPlayed,
          screenEdits: screenEdits,
          format,
        });
      } else {
        // Use existing query (no edits)
        downloadResult = await reportService.downloadCreativeReport(creativeId, {
          startDate: filters?.startDate || null,
          endDate: filters?.endDate || null,
          cityId: filters?.cityId || null,
          regionId: filters?.regionId || null,
          format,
        });
      }

      // Format current date as DD_MM_YYYY
      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();
      const formattedDate = `${day}_${month}_${year}`;
      const fileExt = format === "EXCEL" ? "xlsx" : "pdf";
      const fileName = downloadResult.fileName || `AdvertisementSummaryReport_${formattedDate}.${fileExt}`;

      // Convert base64 to blob and download
      const byteCharacters = atob(downloadResult.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadResult.mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${formatLabel} generated and downloaded successfully`);
    } catch (error) {
      console.error(`Error generating ${formatLabel}:`, error);
      toast.error(`Failed to generate ${formatLabel}. Please try again.`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800">
              Creative Report - Edit & Download
            </h2>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <button
                  onClick={handleConfirm}
                  disabled={Object.keys(rowErrors).length > 0}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  title={
                    Object.keys(rowErrors).length > 0
                      ? "Please fix the highlighted values"
                      : "Confirm Changes"
                  }
                >
                  <CheckIcon className="h-5 w-5" />
                  <span>Confirm</span>
                </button>
              ) : (
                <button
                  onClick={handleGenerateReport}
                  disabled={isGeneratingPDF}
                  className="px-4 py-2 bg-primary-default text-white rounded hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  title={`Generate ${formatLabel}`}
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  <span>Download {formatLabel}</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {/* Editable View */}
            <div className="space-y-6">
              {/* Creative Details */}
              <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                  <div className="md:col-span-2">
                    {editableData.fileUrl ? (
                      <div className="w-full h-64 md:h-80">
                        <FilePreview
                          name={editableData.creativeName}
                          type={
                            editableData.fileType?.includes("video") ? "video" : "image"
                          }
                          url={getFullMediaUrl(editableData.fileUrl)}
                          className="w-full h-full object-contain border border-gray-200 bg-white rounded-lg"
                        />
                      </div>
                    ) : (
                      <div className="w-full h-64 md:h-80 flex items-center justify-center border border-gray-200 bg-gray-100 rounded-lg">
                        <p className="text-gray-500">No preview available</p>
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-3 space-y-4">
                    <div className="pb-4 border-b border-gray-200">
                      <label className="text-sm font-medium text-gray-500 mb-1 block">
                        Creative Name
                      </label>
                      <p className="text-xl font-semibold text-gray-900">
                        {editableData.creativeName}
                      </p>
                    </div>

                    {/*
                      Dates are shown, not edited. They define the window the
                      server queried, and the download mutation re-runs that
                      query with whatever dates it is sent -- so an edited date
                      would return a different set of rows while the header kept
                      the totals computed from the old ones, and the file would
                      contradict itself. Keeping them fixed makes the round-trip
                      return the same rows, which is what lets this stay a
                      frontend-only feature.
                    */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500 mb-1 block">
                          Start Date
                        </label>
                        <p className="text-base text-gray-900">
                          {formatDisplayDate(editableData.startDate)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 mb-1 block">
                          End Date
                        </label>
                        <p className="text-base text-gray-900">
                          {formatDisplayDate(editableData.endDate)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500 mb-1 block">
                          Total No of Screens
                        </label>
                        <p className="text-lg font-semibold text-gray-900">
                          {calculatedTotals.totalScreensPlayed}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 mb-1 block">
                          Number of times Ad played
                        </label>
                        <p className="text-lg font-semibold text-gray-900">
                          {calculatedTotals.numberOfTimesPlayed}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500 mb-1 block">
                          Total screen time of your ads
                        </label>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatDuration(calculatedTotals.totalScreenTime)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 mb-1 block">
                          Average Ad repetition frequency
                        </label>
                        {isEditing ? (
                          <div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={
                                  averageDraft ??
                                  String(Math.round(perPlaySeconds * 100) / 100)
                                }
                                onChange={(e) => handleAverageInput(e.target.value)}
                                onBlur={() => setAverageDraft(null)}
                                className="w-32 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-default"
                              />
                              <span className="text-sm text-gray-500">sec per play</span>
                            </div>
                            {/*
                              What the file will actually print. The server
                              recomputes it as total / plays, so rounding in the
                              rows can leave it a hair off the figure typed
                              above -- showing both is what stops the modal and
                              the PDF disagreeing.
                            */}
                            <p className="mt-1 text-xs text-gray-500">
                              Report will show{" "}
                              {formatAverageFrequency(
                                calculatedTotals.averageRepetitionFrequency
                              )}
                            </p>
                          </div>
                        ) : (
                          <p className="text-lg font-semibold text-gray-900">
                            {formatAverageFrequency(calculatedTotals.averageRepetitionFrequency)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Screens Played Table */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Performance Data ({editableData.screensPlayed.length})
                  </h2>
                  {isEditing && (
                    <button
                      onClick={handleAddRow}
                      className="px-3 py-2 border border-primary-default text-primary-default rounded hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                      title="Add a row to this report only"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Add Row</span>
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-blue-100">
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          Restaurant Name
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Region</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">City</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          Duration
                          {isEditing && maxRowDuration !== null && (
                            <span className="block text-xs font-normal text-gray-500">
                              max {formatDuration(maxRowDuration)}
                            </span>
                          )}
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          Number of times played
                        </th>
                        {isEditing && (
                          <th className="border border-gray-300 px-4 py-2 w-12" />
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {editableData.screensPlayed.map((screen, index) => (
                        <tr key={index}>
                          {/*
                            Only rows added here get editable labels. The rows
                            the server sent describe real screens, and letting
                            those be relabelled would misattribute plays that
                            actually happened somewhere else.
                          */}
                          {(["restaurantName", "region", "city"] as const).map(
                            (field) => (
                              <td
                                key={field}
                                className="border border-gray-300 px-4 py-2 align-top"
                              >
                                {isEditing && isAddedRow(screen) ? (
                                  <input
                                    type="text"
                                    value={screen[field] || ""}
                                    onChange={(e) =>
                                      handleRowTextChange(index, field, e.target.value)
                                    }
                                    placeholder={
                                      field === "restaurantName"
                                        ? "Restaurant name"
                                        : field === "region"
                                        ? "Region"
                                        : "City"
                                    }
                                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-default"
                                  />
                                ) : (
                                  screen[field] || "-"
                                )}
                              </td>
                            )
                          )}
                          <td className="border border-gray-300 px-4 py-2 align-top">
                            {isEditing ? (
                              <div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    value={cellValue(index, "duration", screen.duration)}
                                    onChange={(e) =>
                                      handleCellInput(index, "duration", e.target.value)
                                    }
                                    onBlur={() => handleCellBlur(index, "duration")}
                                    className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 ${
                                      rowErrors[index]
                                        ? "border-danger-default focus:ring-danger-default"
                                        : "border-gray-300 focus:ring-primary-default"
                                    }`}
                                    min="0"
                                    placeholder="Seconds"
                                  />
                                  <span className="text-sm text-gray-500 whitespace-nowrap">
                                    ({formatDuration(screen.duration)})
                                  </span>
                                </div>
                                {rowErrors[index] && (
                                  <p className="mt-1 text-sm text-danger-default">
                                    {rowErrors[index]}
                                  </p>
                                )}
                              </div>
                            ) : (
                              formatDuration(screen.duration)
                            )}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 align-top">
                            {isEditing ? (
                              <input
                                type="number"
                                value={cellValue(
                                  index,
                                  "numberOfTimesPlayed",
                                  screen.numberOfTimesPlayed
                                )}
                                onChange={(e) =>
                                  handleCellInput(
                                    index,
                                    "numberOfTimesPlayed",
                                    e.target.value
                                  )
                                }
                                onBlur={() =>
                                  handleCellBlur(index, "numberOfTimesPlayed")
                                }
                                className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 ${
                                  rowErrors[index]
                                    ? "border-danger-default focus:ring-danger-default"
                                    : "border-gray-300 focus:ring-primary-default"
                                }`}
                                min="0"
                              />
                            ) : (
                              screen.numberOfTimesPlayed
                            )}
                          </td>
                          {isEditing && (
                            <td className="border border-gray-300 px-2 py-2 text-center align-top">
                              <button
                                onClick={() => handleRemoveRow(index)}
                                className="p-1 text-gray-400 hover:text-danger-default transition-colors"
                                title="Remove this row"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          {isEditing ? (
            <div className="p-4 bg-blue-50 border-t border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Edit Mode:</strong> Set the average seconds per play and every row's screen
                time follows; editing a row's Duration or Number of Times Played updates the other.
                Dates are fixed to the range this report was generated for. Added rows appear in this
                downloaded file only -- nothing here changes the recorded data. Click "Confirm" when
                you're done editing.
              </p>
            </div>
          ) : (
            <div className="p-4 bg-green-50 border-t border-green-200">
              <p className="text-sm text-green-800">
                <strong>Ready to Download:</strong> Your changes have been confirmed. Click "Download {formatLabel}" to generate and download the report.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditableCreativeReport;
